// Pictures of things to count: base-ten blocks, counters in groups and
// arrays. Plain React SVG, like practice-instrument.tsx. Every thing is
// drawn on its own and nothing is numbered: counting them is the question.

import type { ReactNode } from 'react';
import {
  arrayPicture,
  baseTenPicture,
  countersPicture,
  describeCount,
  type ArraySpec,
  type BaseTenPicture,
  type BaseTenSpec,
  type CountItem,
  type CountSpec,
  type CountersSpec,
} from './practice-count-data';

const INK = '#2d4a3e';
const BLOCK_FILL = '#cfe3d4';
const GROUP_FILL = '#f4f8f4';

// One flat drawing of each thing, filling a square of side `size` whose top
// left corner is (x, y).
function ItemIcon({
  item,
  x,
  y,
  size,
}: {
  item: CountItem;
  x: number;
  y: number;
  size: number;
}) {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const r = size / 2;
  const line = {
    stroke: INK,
    strokeWidth: 1.5,
    strokeLinejoin: 'round' as const,
  };
  switch (item) {
    case 'apple':
      return (
        <g data-item="apple">
          <circle
            cx={cx}
            cy={cy + r * 0.12}
            r={r * 0.78}
            fill="#e0605a"
            {...line}
          />
          <path
            d={`M ${String(cx)} ${String(cy - r * 0.6)} L ${String(cx + r * 0.1)} ${String(cy - r)}`}
            {...line}
            fill="none"
          />
          <ellipse
            cx={cx + r * 0.35}
            cy={cy - r * 0.8}
            rx={r * 0.25}
            ry={r * 0.12}
            fill="#6aa86a"
          />
        </g>
      );
    case 'star': {
      const points = Array.from({ length: 10 }, (_, index) => {
        const turn = (index * Math.PI) / 5;
        const reach = index % 2 === 0 ? r * 0.95 : r * 0.42;
        return `${(cx + Math.sin(turn) * reach).toFixed(2)},${(cy - Math.cos(turn) * reach).toFixed(2)}`;
      }).join(' ');
      return (
        <g data-item="star">
          <polygon points={points} fill="#f2c94c" {...line} />
        </g>
      );
    }
    case 'counter':
      return (
        <g data-item="counter">
          {/* A plain solid purple disc, unlike any coin or the red apple. */}
          <circle cx={cx} cy={cy} r={r * 0.85} fill="#7b5ea7" {...line} />
        </g>
      );
    case 'block':
      return (
        <g data-item="block">
          <rect
            x={x + r * 0.15}
            y={y + r * 0.15}
            width={size - r * 0.3}
            height={size - r * 0.3}
            rx="3"
            fill="#6fa8dc"
            {...line}
          />
        </g>
      );
    case 'fish':
      return (
        <g data-item="fish">
          <ellipse
            cx={cx - r * 0.12}
            cy={cy}
            rx={r * 0.65}
            ry={r * 0.42}
            fill="#f39c5a"
            {...line}
          />
          <polygon
            points={`${String(cx + r * 0.5)},${String(cy)} ${String(cx + r * 0.95)},${String(cy - r * 0.4)} ${String(cx + r * 0.95)},${String(cy + r * 0.4)}`}
            fill="#f39c5a"
            {...line}
          />
          <circle
            cx={cx - r * 0.45}
            cy={cy - r * 0.08}
            r={r * 0.08}
            fill={INK}
          />
        </g>
      );
    case 'ball':
      return (
        <g data-item="ball">
          <circle cx={cx} cy={cy} r={r * 0.85} fill="#8fc0e8" {...line} />
          <path
            d={`M ${String(cx - r * 0.85)} ${String(cy)} Q ${String(cx)} ${String(cy + r * 0.5)} ${String(cx + r * 0.85)} ${String(cy)}`}
            fill="none"
            stroke={INK}
            strokeWidth="1"
          />
        </g>
      );
    case 'pencil':
      return (
        <g data-item="pencil">
          <polygon
            points={`${String(x + r * 0.2)},${String(cy - r * 0.25)} ${String(x + size - r * 0.55)},${String(cy - r * 0.25)} ${String(x + size - r * 0.1)},${String(cy)} ${String(x + size - r * 0.55)},${String(cy + r * 0.25)} ${String(x + r * 0.2)},${String(cy + r * 0.25)}`}
            fill="#f2c94c"
            {...line}
          />
        </g>
      );
    case 'flower':
      return (
        <g data-item="flower">
          {[0, 72, 144, 216, 288].map((angle) => {
            const turn = (angle * Math.PI) / 180;
            return (
              <circle
                key={angle}
                cx={cx + Math.sin(turn) * r * 0.48}
                cy={cy - Math.cos(turn) * r * 0.48}
                r={r * 0.36}
                fill="#e98bbf"
                {...line}
              />
            );
          })}
          <circle cx={cx} cy={cy} r={r * 0.28} fill="#f2c94c" {...line} />
        </g>
      );
  }
}

function Frame({
  spec,
  width,
  height,
  children,
}: {
  spec: CountSpec;
  width: number;
  height: number;
  children: ReactNode;
}) {
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describeCount(spec)}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(width * 1.3))}px` }}
      >
        {children}
      </svg>
    </figure>
  );
}

function SmallCubes({
  x,
  y,
  across,
  down,
  unit,
}: {
  x: number;
  y: number;
  across: number;
  down: number;
  unit: number;
}) {
  return (
    <>
      <rect
        x={x}
        y={y}
        width={across * unit}
        height={down * unit}
        fill={BLOCK_FILL}
        stroke={INK}
        strokeWidth="1.5"
      />
      {Array.from({ length: across - 1 }, (_, index) => (
        <line
          key={`a${String(index)}`}
          x1={x + (index + 1) * unit}
          y1={y}
          x2={x + (index + 1) * unit}
          y2={y + down * unit}
          stroke={INK}
          strokeWidth="0.6"
        />
      ))}
      {Array.from({ length: down - 1 }, (_, index) => (
        <line
          key={`d${String(index)}`}
          x1={x}
          y1={y + (index + 1) * unit}
          x2={x + across * unit}
          y2={y + (index + 1) * unit}
          stroke={INK}
          strokeWidth="0.6"
        />
      ))}
    </>
  );
}

function BaseTenView({ spec }: { spec: BaseTenSpec }) {
  const picture: BaseTenPicture = baseTenPicture(spec);
  const { unit, depth } = picture;
  const side = unit * 10;
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.pieces.map((piece, index) => {
        const key = `${piece.kind}${String(index)}`;
        if (piece.kind === 'thousand') {
          const front = { x: piece.x, y: piece.y + depth };
          return (
            <g key={key} data-piece="thousand">
              <polygon
                points={`${String(front.x)},${String(front.y)} ${String(front.x + depth)},${String(piece.y)} ${String(front.x + side + depth)},${String(piece.y)} ${String(front.x + side)},${String(front.y)}`}
                fill={BLOCK_FILL}
                stroke={INK}
                strokeWidth="1.5"
              />
              <polygon
                points={`${String(front.x + side)},${String(front.y)} ${String(front.x + side + depth)},${String(piece.y)} ${String(front.x + side + depth)},${String(piece.y + side)} ${String(front.x + side)},${String(front.y + side)}`}
                fill={BLOCK_FILL}
                stroke={INK}
                strokeWidth="1.5"
              />
              <SmallCubes
                x={front.x}
                y={front.y}
                across={10}
                down={10}
                unit={unit}
              />
            </g>
          );
        }
        if (piece.kind === 'hundred') {
          return (
            <g key={key} data-piece="hundred">
              <SmallCubes
                x={piece.x}
                y={piece.y}
                across={10}
                down={10}
                unit={unit}
              />
            </g>
          );
        }
        if (piece.kind === 'ten') {
          return (
            <g key={key} data-piece="ten">
              <SmallCubes
                x={piece.x}
                y={piece.y}
                across={1}
                down={10}
                unit={unit}
              />
            </g>
          );
        }
        return (
          <g key={key} data-piece="one">
            <SmallCubes
              x={piece.x}
              y={piece.y}
              across={1}
              down={1}
              unit={unit}
            />
          </g>
        );
      })}
    </Frame>
  );
}

function CountersView({ spec }: { spec: CountersSpec }) {
  const picture = countersPicture(spec);
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.groups.map((group, index) => (
        <g key={index} data-group={index + 1}>
          <rect
            x={group.x}
            y={group.y}
            width={group.width}
            height={group.height}
            rx="10"
            fill={GROUP_FILL}
            stroke={INK}
            strokeWidth="1.5"
          />
          {group.items.map((item, at) => (
            <ItemIcon
              key={at}
              item={item.item}
              x={item.x}
              y={item.y}
              size={picture.size}
            />
          ))}
        </g>
      ))}
    </Frame>
  );
}

function ArrayView({ spec }: { spec: ArraySpec }) {
  const picture = arrayPicture(spec);
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.items.map((item, index) => (
        <ItemIcon
          key={index}
          item={item.item}
          x={item.x}
          y={item.y}
          size={picture.size}
        />
      ))}
    </Frame>
  );
}

export function CountPictureView({ count }: { count: CountSpec }) {
  switch (count.type) {
    case 'base_ten':
      return <BaseTenView spec={count} />;
    case 'counters':
      return <CountersView spec={count} />;
    case 'array':
      return <ArrayView spec={count} />;
  }
}
